import { defineStore } from 'pinia';

const DEFAULT_URL =
  import.meta.env.VITE_WEBSOCKET_URL || 'wss://pbxws-dev.dewasyscrm.com/ws';
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY_MS = 3000;

function log(...args) {
  console.log('[VoipSocket]', ...args);
}

/**
 * Generic WebSocket store for the voip socket server.
 * Use this for a single shared connection; register open/message handlers for different features
 * (e.g. agent status, future notifications). Handlers are called when the socket opens or when
 * a message is received (parsed as JSON). Scale by adding more registerMessageHandler callbacks
 * that filter by message type.
 */
export const useVoipSocketStore = defineStore('voipSocket', {
  state: () => ({
    isConnected: false,
    _ws: null,
    _reconnectAttempts: 0,
    _openHandlers: [],
    _messageHandlers: [],
    _url: DEFAULT_URL,
  }),

  actions: {
    _handleOpen() {
      log('Connected', this._url);
      this.isConnected = true;
      this._reconnectAttempts = 0;
      this._openHandlers.forEach((fn) => {
        try {
          fn(this._ws);
        } catch (err) {
          console.error('[VoipSocket] Open handler error:', err);
        }
      });
    },

    _handleMessage(event) {
      try {
        const data = JSON.parse(event.data);
        this._messageHandlers.forEach((fn) => {
          try {
            fn(data);
          } catch (err) {
            console.error('[VoipSocket] Message handler error:', err);
          }
        });
      } catch (err) {
        console.error('[VoipSocket] Parse message error:', err);
      }
    },

    _handleClose(ev) {
      log('Disconnected', { code: ev?.code, reason: ev?.reason });
      this.isConnected = false;
      this._ws = null;
      if (this._reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        this._reconnectAttempts++;
        setTimeout(() => this.connect(), RECONNECT_DELAY_MS);
      }
    },

    _handleError() {
      this.isConnected = false;
    },

    /**
     * Register a handler called when the socket opens. Receives the WebSocket instance.
     * @returns {Function} Unsubscribe function
     */
    registerOpenHandler(fn) {
      this._openHandlers.push(fn);
      return () => {
        this._openHandlers = this._openHandlers.filter((f) => f !== fn);
      };
    },

    /**
     * Register a handler called for every message (parsed JSON).
     * @returns {Function} Unsubscribe function
     */
    registerMessageHandler(fn) {
      this._messageHandlers.push(fn);
      return () => {
        this._messageHandlers = this._messageHandlers.filter((f) => f !== fn);
      };
    },

    /**
     * Connect to the WebSocket. Idempotent if already open or connecting.
     * @param {Object} [options] Optional: { url } to override default URL
     */
    connect(options = {}) {
      const url = options.url ?? this._url;
      const state = this._ws?.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        return;
      }
      log('Connecting to', url);
      try {
        this._url = url;
        this._ws = new WebSocket(url);
        this._ws.onopen = () => this._handleOpen();
        this._ws.onmessage = (e) => this._handleMessage(e);
        this._ws.onclose = (ev) => this._handleClose(ev);
        this._ws.onerror = () => this._handleError();
      } catch (err) {
        console.error('[VoipSocket] Connect failed:', err);
        this.isConnected = false;
      }
    },

    /**
     * Send a JSON-serializable payload.
     */
    send(data) {
      if (this._ws?.readyState === WebSocket.OPEN) {
        this._ws.send(JSON.stringify(data));
      }
    },

    disconnect() {
      if (this._ws) {
        if (this._ws.readyState === WebSocket.OPEN) {
          this._ws.send(JSON.stringify({ type: 'disconnect' }));
        }
        this._ws.close();
        this._ws = null;
      }
      this.isConnected = false;
      this._reconnectAttempts = MAX_RECONNECT_ATTEMPTS;
    },
  },
});
